import { forwardRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import PasswordToggleIcon from "@/app/(security)/inscription/password-toggle-icon";
import { InputWrapper } from "@/components/global/form/inputs/input-wrapper";
import {
  BaseInputStyleProps,
  baseInputStylesMap,
  defaultBaseInputStyles,
} from "@/components/global/form/inputs/styles/base-input.styles";
import { InputWrapperStyleProps } from "@/components/global/form/inputs/styles/input-wrapper.styles";
import Icon from "@/components/icon";

interface PasswordInputProps {
  error?: string;
  isValid?: boolean;
  required?: boolean;
  disabled?: boolean;
  dataCy?: string;
  placeholder?: string;
  wrapperStyles?: InputWrapperStyleProps;
  styles?: BaseInputStyleProps;
  [key: string]: any;
}

/**
 * Password input field component with toggle visibility and validation states
 * @param {string} [error] - Error message to display
 * @param {boolean} [isValid] - Whether the input is valid
 * @param {boolean} [required] - Whether the input is required
 * @param {boolean} [disabled] - Whether the input is disabled
 * @param {string} [dataCy] - Data-cy attribute for testing
 * @param {string} [placeholder] - Input placeholder text
 * @param {InputWrapperStyleProps} [wrapperStyles] - Styles for the input wrapper
 * @param {BaseInputStyleProps} [styles] - Custom styles for the input
 * @example
 * <PasswordInput
 *   placeholder="Enter your password"
 *   error={errorMessage}
 *   isValid={isValid}
 *   styles={{
 *     textColor: "primary",
 *     placeholderStyle: "italic",
 *     padding: { left: "md", right: "lg" }
 *   }}
 *   wrapperStyles={{
 *     height: "md",
 *     rounded: "lg"
 *   }}
 * />
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      error,
      isValid,
      required: _required,
      disabled,
      dataCy,
      styles,
      wrapperStyles,
      ...props
    }: PasswordInputProps,
    ref,
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const mergedStyles = { ...defaultBaseInputStyles, ...styles };

    return (
      <InputWrapper error={error} disabled={disabled} styles={wrapperStyles}>
        <input
          ref={ref}
          type={showPassword ? "text" : "password"}
          className={twMerge(
            "w-full bg-transparent autofill:bg-transparent rounded-md",
            mergedStyles.placeholderStyle === "italic" && "placeholder:italic",
            baseInputStylesMap.padding[mergedStyles.padding?.left || "md"],
            baseInputStylesMap.padding[mergedStyles.padding?.right || "lg"],
            error && `text-${mergedStyles.textColor}`,
            disabled && "cursor-not-allowed",
            mergedStyles.className,
          )}
          disabled={disabled}
          data-cy={dataCy}
          {...props}
        />
        <div className="flex items-center gap-2 absolute right-2">
          <PasswordToggleIcon
            showPassword={showPassword}
            togglePasswordShow={() => setShowPassword(!showPassword)}
          />
          {error && <Icon icon="cross" size={25} className="text-primary" />}
          {isValid && !error && (
            <Icon icon="check-simple" size={25} className="text-success2" />
          )}
        </div>
      </InputWrapper>
    );
  },
);

PasswordInput.displayName = "PasswordInput";
